import { Injectable, Injector, TemplateRef, inject } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Dialog, DialogRef as CdkDialogRef } from '@angular/cdk/dialog';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, ComponentType } from '@angular/cdk/portal';

/** Below Tailwind's md breakpoint (768px; tailwind.config.js keeps the default
 *  screens), the width where the max-md: classes apply. */
export const PHONE = '(max-width: 767.98px)';

export interface DialogRef<R = any> {
  close: (result?: R) => void;
}

@Injectable({ providedIn: 'root' })
export class AppDialogService {
  private overlay = inject(Overlay);
  private injector = inject(Injector);
  private dialog = inject(Dialog);
  private breakpoints = inject(BreakpointObserver);

  /**
   * A template or a component as a 480 px dialog on desktop and a full-width sheet
   * docked at the bottom on a phone, switching live when the width crosses md. CDK
   * Dialog brings the backdrop, scroll lock, focus trap, Escape and focus return;
   * data reaches a component as DIALOG_DATA, and disableClose keeps a backdrop
   * click from closing it (the content closes itself).
   */
  openSheet<R = unknown, D = unknown>(
    content: TemplateRef<unknown> | ComponentType<unknown>,
    options: { ariaLabel: string; data?: D; disableClose?: boolean; restoreFocus?: boolean }
  ): CdkDialogRef<R> {
    const ref = this.dialog.open<R, D>(content, {
      ariaLabel: options.ariaLabel,
      data: options.data,
      disableClose: options.disableClose,
      restoreFocus: options.restoreFocus ?? true,
      maxWidth: '100vw',
      backdropClass: 'cdk-overlay-dark-backdrop',
    });
    const fit = this.breakpoints.observe(PHONE).subscribe(({ matches }) => {
      // Size first: the position strategy reads the width when it is applied
      ref.updateSize(matches ? '100%' : '480px');
      const position = this.overlay.position().global().centerHorizontally();
      ref.overlayRef.updatePositionStrategy(
        matches ? position.bottom('0') : position.centerVertically()
      );
    });
    ref.closed.subscribe(() => fit.unsubscribe());
    return ref;
  }

  open<R = any, T extends object = any>(
    component: ComponentType<T>,
    data?: Partial<T>
  ): DialogRef<R> {
    const positionStrategy = this.overlay
      .position()
      .global()
      .centerHorizontally()
      .centerVertically();
    const overlayRef: OverlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-dark-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.block(),
    });

    const portal = new ComponentPortal(
      component,
      null,
      Injector.create({
        parent: this.injector,
        providers: [{ provide: 'DIALOG_DATA', useValue: data ?? {} }],
      })
    );
    const compRef = overlayRef.attach(portal);
    if (data) {
      for (const [k, v] of Object.entries(data)) (compRef.instance as any)[k] = v;
    }

    const ref: DialogRef<R> = {
      close: () => overlayRef.dispose(),
    };
    overlayRef.backdropClick().subscribe(() => ref.close());
    return ref;
  }
}
